/**
 * Grouped table view — group header rows + per-group numeric aggregate rows +
 * a grand-total footer. Used when group-by is active (computed over ALL fetched
 * rows). Non-virtualized: grouped output is summary-oriented.
 */
import { useState } from 'react'
import { ChevronDown, ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { ExplorerCell } from './explorer-cell'
import type { ResolvedColumn, ResolvedRow } from '../lib/spec-resolver'
import type { GroupedResult, GroupAggregate } from '../lib/group-by'

interface GroupedTableProps {
  columns: ResolvedColumn[]
  grouped: GroupedResult
  groupColId: string
  onRowClick?: (row: ResolvedRow) => void
}

function AggRow({ columns, agg, label, leadColId }: { columns: ResolvedColumn[]; agg: GroupAggregate; label: string; leadColId: string }) {
  return (
    <tr className="bg-muted/40 border-b border-border text-xs">
      {columns.map((c, i) => {
        if (c.id === leadColId || (i === 0 && leadColId === '')) {
          return <td key={c.id} className="px-4 py-1.5 font-medium text-muted-foreground">{label}</td>
        }
        const sum = agg.sums[c.id]
        const avg = agg.avgs[c.id]
        if (sum === undefined) return <td key={c.id} />
        return (
          <td key={c.id} className="px-4 py-1.5 text-muted-foreground tabular-nums">
            Σ {sum} · ⌀ {avg}
          </td>
        )
      })}
    </tr>
  )
}

export function GroupedTable({ columns, grouped, onRowClick }: GroupedTableProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const lead = columns[0]?.id ?? ''

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full border-collapse min-w-max">
        <thead>
          <tr className="bg-muted sticky top-0 z-10">
            {columns.map((c) => (
              <th key={c.id} className="px-4 py-2.5 text-start text-xs font-semibold text-muted-foreground border-b border-border whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grouped.groups.map((g) => {
            const isCol = collapsed[g.key]
            return (
              <FragmentGroup key={g.key} cols={columns.length}>
                <tr
                  className="bg-card border-b border-border cursor-pointer hover:bg-muted/30"
                  onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                >
                  <td colSpan={columns.length} className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
                      {isCol ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {g.label}
                      <span className="text-xs text-muted-foreground font-normal">({g.count})</span>
                    </span>
                  </td>
                </tr>
                {!isCol && g.rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn('border-b border-border/50 hover:bg-muted/40', onRowClick && 'cursor-pointer')}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <td key={col.id} className="px-4 py-2 text-sm text-foreground whitespace-nowrap">
                        <ExplorerCell column={col} row={row} />
                      </td>
                    ))}
                  </tr>
                ))}
                {!isCol && <AggRow columns={columns} agg={g.agg} label="جمع گروه" leadColId={lead} />}
              </FragmentGroup>
            )
          })}
        </tbody>
        <tfoot>
          <tr className="bg-muted border-t-2 border-border text-xs font-semibold">
            {columns.map((c, i) => {
              if (i === 0) return <td key={c.id} className="px-4 py-2">جمع کل ({grouped.totalCount})</td>
              const sum = grouped.total.sums[c.id]
              if (sum === undefined) return <td key={c.id} />
              return <td key={c.id} className="px-4 py-2 tabular-nums text-foreground">Σ {sum} · ⌀ {grouped.total.avgs[c.id]}</td>
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function FragmentGroup({ children }: { children: React.ReactNode; cols: number }) {
  return <>{children}</>
}
